import React, { useState } from 'react';
import { Navbar } from '../components/layouts/Navbar';
import { PlannerSidebar } from '../components/layouts/PlannerSidebar';
import { ParcelaTable } from '../components/parcela/ParcelaTable';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import ApoliceFormModal from '../components/apolice/ApoliceFormModal';
import { useNotifier } from '../components/ui/Notifier';
import { parcelaListService } from '../services/parcelaService';
import type { ParcelaData } from '../services/parcelaService';
import type { ApoliceData } from '../services/apoliceService';

const Parcelas: React.FC = () => {
  // debug: log imported component types to detect undefined imports
  // (will be removed after verification)
  // eslint-disable-next-line no-console
  console.log('COMPONENT TYPES', {
    Navbar: typeof Navbar,
    PlannerSidebar: typeof PlannerSidebar,
    ParcelaTable: typeof ParcelaTable,
    ApoliceFormModal: typeof ApoliceFormModal
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | undefined>();
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [navigatorFn, setNavigatorFn] = useState<((currentId?: string) => Promise<ParcelaData | undefined>) | null>(null);
  const [selectedParcelaId, setSelectedParcelaId] = useState<string | null>(null);
  const [showDefaultFilterDialog, setShowDefaultFilterDialog] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetParcela, setDeleteTargetParcela] = useState<ParcelaData | null>(null);
  const notifier = useNotifier();

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEdit = (parcela: ParcelaData) => {
    // Usar os dados do documento que já vêm na parcela
    if (parcela.documento) {
      setSelectedApolice(parcela.documento as any);
      setSelectedParcelaId(parcela.id);
      setIsEditing(true);
      setShowFormModal(true);
    } else {
      console.error('Parcela sem dados do documento:', parcela);
      notifier.show({ message: 'Dados do documento não disponíveis', severity: 'warning' });
    }
  };

  const handleView = (parcela: ParcelaData) => {
    // Usar os dados do documento que já vêm na parcela
    if (parcela.documento) {
      setSelectedApolice(parcela.documento as any);
      setSelectedParcelaId(parcela.id);
      setIsEditing(false);
      setShowFormModal(true);
    } else {
      console.error('Parcela sem dados do documento:', parcela);
      notifier.show({ message: 'Dados do documento não disponíveis', severity: 'warning' });
    }
  };

  const handleDelete = (parcela: ParcelaData) => {
    setDeleteTargetParcela(parcela);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (!deleteTargetParcela) return;
    try {
      await parcelaListService.delete(deleteTargetParcela.id);
      notifier.show({ message: 'Parcela excluída com sucesso', severity: 'success' });
      handleRefresh();
    } catch (error: any) {
      console.error('Erro ao excluir parcela:', error);
      notifier.show({
        message: error?.message ? `Erro ao excluir parcela: ${error.message}` : 'Erro ao excluir parcela',
        severity: 'error'
      });
    } finally {
      setDeleteTargetParcela(null);
    }
  };

  const handleFormModalClose = () => {
    setShowFormModal(false);
    setSelectedApolice(undefined);
    setSelectedParcelaId(null);
    setIsEditing(false);
  };

  const handleFormSave = () => {
    // Atualizar a tabela após salvar
    handleRefresh();
  };

  const handleFormUpdated = () => {
    // Atualizar a tabela após editar
    handleRefresh();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4EFFA' }}>
      <Navbar />
      <div className="flex p-6 gap-3">
        <div className="flex-1">
          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-xs font-semibold" style={{ color: '#3d2b56' }}>
                Gestão de Parcelas
              </h1>
              <p className="text-xs mt-1" style={{ color: '#9E9E9E' }}>
                Gerencie as parcelas dos documentos com filtros por data de vencimento e pagamento
              </p>
            </div>
          </div>

          {/* Conteúdo principal */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <ParcelaTable
              onEdit={handleEdit}
              onView={handleView}
              onDelete={handleDelete}
              onRefresh={handleRefresh}
              refreshTrigger={refreshTrigger}
              activeId={selectedParcelaId}
              onRegisterNavigator={(fn) => setNavigatorFn(() => fn)}
            />
          </div>
          <ConfirmDialog
            isOpen={deleteConfirmOpen}
            title="Excluir Parcela"
            message={deleteTargetParcela ? `Deseja realmente excluir a parcela ${deleteTargetParcela.numeroParcela} do documento ${deleteTargetParcela.numeroDocumento}?` : 'Excluir parcela?'}
            confirmText="Excluir"
            cancelText="Cancelar"
            confirmButtonColor="red"
            onConfirm={handleConfirmDelete}
            onCancel={() => { setDeleteConfirmOpen(false); setDeleteTargetParcela(null); }}
          />
          <ConfirmDialog
            isOpen={showDefaultFilterDialog}
            title="Filtro padrão: Em Aberto"
            message="Por padrão a lista exibirá parcelas em aberto até a data atual."
            details={["Você pode alterar este comportamento usando os filtros (Situação Pagamento → Em Aberto/Todas/Pagas)."]}
            onConfirm={() => setShowDefaultFilterDialog(false)}
            onCancel={() => setShowDefaultFilterDialog(false)}
            confirmText="Entendi"
            confirmButtonColor="#7A61B2"
          />
        </div>
        
        <div className="hidden lg:block">
          <PlannerSidebar />
        </div>
      </div>

      {/* Modal de Formulário da Apólice */}
      <ApoliceFormModal
        apolice={selectedApolice}
        isOpen={showFormModal}
        isEditing={isEditing}
        onClose={handleFormModalClose}
        onSave={handleFormSave}
        onUpdated={handleFormUpdated}
        openParcelasOnOpen={true}
        onNextRequested={async (currentId?: string) => {
          if (navigatorFn) {
            const nextParcela = await navigatorFn(selectedParcelaId || undefined);
            if (nextParcela && nextParcela.documento) {
              // Usar os dados do documento que já vêm na próxima parcela
              setSelectedApolice(nextParcela.documento as any);
              setSelectedParcelaId(nextParcela.id);
              return nextParcela.documento as any;
            }
          }
          return undefined;
        }}
      />
    </div>
  );
};

export default Parcelas;